import {
  Box,
  CircularProgress,
  Container,
  createTheme,
  Link,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../hooks/useAuthStore';

const UnauthenticatedLayout = () => {
  const authStore = useAuthStore();
  const { t } = useTranslation();
  const location = useLocation();

  if (!authStore.isInitialized) {
    return (
      <Box>
        <Box
          component="main"
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (authStore.getIsAuthenticated()) {
    return <Navigate to="/" />;
  }
  const theme = createTheme({
    palette: {
      primary: {
        main: '#ffffffed',
      },
      text: {
        primary: '#ffffffed',
        secondary: '#ffffffed',
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: '#ffffff',
            zIndex: 2,
            '&.Mui-focused': {
              color: '#90caf9',
              zIndex: 2,
            },
            '&.Mui-disabled': {
              color: '#888888',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            position: 'relative',
            zIndex: 1,
            color: '#ffffff',
            backgroundColor: 'rgba(30, 30, 30, 0.5)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#bbbbbb',
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
              },
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ffffff',
              WebkitTextFillColor: '#ffffff !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#90caf9',
              color: '#ffffff',
              WebkitTextFillColor: '#ffffff !important',
            },
            '&.Mui-disabled': {
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#bebebe',
              },
            },
          },
          input: {
            color: '#ffffff !important',
            caretColor: '#90caf9',
            position: 'relative',
            zIndex: 2,
            '&.Mui-disabled': {
              color: '#ffffff !important',
            },
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          root: {
            color: '#ffffffed',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: '#ffffffed',
          },
        },
      },
    },
  });
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundImage: 'url(/img/background.png)',
          'background-repeat': 'no-repeat',
          'background-size': 'cover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100vh',
        }}
      >
        <Container component="main" maxWidth="xs">
          {/* main container */}
          <Box
            sx={{
              paddingTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* lock avatar */}
            <Box sx={{ m: 2, mb: '2rem' }}>
              <img src="/img/SIQ_logo.png" alt="logo" style={{ width: '9.625rem' }} />
            </Box>
            {/* title text */}
            <Typography component="h1" variant="h5">
              {location.pathname.includes('forgot-password') &&
                t('Forgot password', { defaultValue: 'Forgot password' })}
              {location.pathname.includes('reset-password') &&
                t('Password reset', { defaultValue: 'Password reset' })}
              {location.pathname.includes('finish-registration') &&
                t('Finish registration', { defaultValue: 'Finish registration' })}
            </Typography>
            {/* form container */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: '1rem',
              }}
            >
              <Outlet />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 8, mb: 4 }}>
            {`${t('Copyright', { defaultValue: 'Copyright' })} © `}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link color="inherit">SIQ</Link> {new Date().getFullYear()}
          </Typography>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default UnauthenticatedLayout;
